import React from 'react';

import { loadBannerFamily } from '../banner-family-load';
import {
  creativesServiceConfigured,
  fetchCreativeFamilyFromService,
  type CreativeFamily,
  type CreativeItem,
} from '../creative-api';

export interface BrandPromotionPool {
  /** The brand's promotions (every placement's items, as the Marketing Assets libraries get them). */
  items: CreativeItem[];
  /** This brand's read has answered (an answer or a failure): an empty list is then a real one. */
  loaded: boolean;
}

/** One empty pool for every not-yet-read render (a stable identity for the composers' effects). */
const NO_ITEMS: CreativeItem[] = [];

/** The pool a composer edits against: every placement's items, none for the logo family. Pure. */
const poolOf = (family: CreativeFamily | undefined): CreativeItem[] =>
  family && family.source !== 'logos' ? family.placements.flatMap((p) => p.items) : [];

/**
 * The brand's promotion pool, read once per brand: the same read the Creatives page opens with (`loadBannerFamily`
 * over spark-creatives, as the Website hub's Creatives card does), WITHOUT `useCreatives`' run machinery (resuming a
 * stored run, polling, refetching as runs land). For a surface that only needs the pool to hand to a composer, such
 * as the in-place asset overlay. A failed read answers an empty pool (the composers handle an empty one).
 */
export const useBrandPromotionPool = (tenantId: number | undefined, domain: string | undefined): BrandPromotionPool => {
  const [read, setRead] = React.useState<{ key: string; items: CreativeItem[] } | null>(null);
  const key = tenantId ? `${tenantId}|${domain ?? ''}` : '';
  React.useEffect(() => {
    // Syncs with spark-creatives: the brand's banner family, read again on a brand switch (the stale read is aborted).
    if (!tenantId) return undefined;
    const controller = new AbortController();
    const readKey = `${tenantId}|${domain ?? ''}`;
    void loadBannerFamily({
      serviceConfigured: creativesServiceConfigured(),
      fetchFromService: () => fetchCreativeFamilyFromService(tenantId, domain, controller.signal),
    }).then((res) => {
      if (!controller.signal.aborted) setRead({ key: readKey, items: res.ok ? poolOf(res.family) : [] });
    });
    return () => controller.abort();
  }, [tenantId, domain]);
  const current = read && read.key === key ? read : null;
  return { items: current?.items ?? NO_ITEMS, loaded: current !== null };
};
